import React, { useMemo } from 'react';

import { Form } from 'antd';
import styled from 'styled-components';

import { getCommunityContractLabel } from 'components/CMASummary/utils';
import { sortCommunityContracts } from 'components/CommunityInfo/careTypes/utils';
import Select from 'components/lib/Select';
import { CommunityContract } from 'types/contracts';

import { useCustomCommunityContractTypeFilterContext } from '../CustomCommunityContractTypeFilterContext';

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
`;

const Dot = styled.span<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ color }) => color};
`;

const NameLabel = styled.span`
  flex: 1;
  font-family: var(--font-regular);
  font-size: 13px;
  color: var(--text-primary);
  white-space: normal;
  word-break: break-word;
`;

const StyledSelect = styled(Select)`
  width: 180px;
  flex-shrink: 0;

  &&&.ant-select {
    height: 36px;
  }

  &&& .ant-select-selector {
    border-radius: 0;
  }

  .ant-select-selection-item {
    font-size: 14px;
  }
`;

type Props = {
  communityId: number;
  name: string;
  color: string;
  contracts: CommunityContract[];
};

const ContractRow = ({ communityId, name, color, contracts }: Props) => {
  const { form } = useCustomCommunityContractTypeFilterContext();
  const fieldName: ['overrides', number] = ['overrides', communityId];
  const selectedValue = Form.useWatch(fieldName, form);

  const options = useMemo(
    () =>
      [...(contracts || [])]
        .filter((contract): contract is CommunityContract & { id: number } => Boolean(contract?.id))
        .sort(sortCommunityContracts)
        .map((contract) => ({
          label: getCommunityContractLabel(contract),
          value: contract.id,
        })),
    [contracts]
  );

  const handleChange = (value: number) => {
    form.setFields([{ name: fieldName, value }]);
  };

  return (
    <Row>
      <Dot color={color} />
      <NameLabel>{name}</NameLabel>
      <Form.Item name={fieldName} noStyle>
        <StyledSelect
          value={selectedValue}
          options={options}
          onChange={handleChange}
          getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
          disabled={options.length === 0}
        />
      </Form.Item>
    </Row>
  );
};

export default ContractRow;
